import { getLuxPlusSubscriptionItems } from 'checkout/selectors/view/luxPlusSubscriptionItems'
import { createSelector } from 'reselect'

export const getLuxPlusSubscriptionRenewalDate = createSelector(
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item?.expiryDate,
  (expiryDate): string | undefined => {
    return expiryDate
  },
)

export const getLuxPlusSubscriptionRenewalOrExpiryDate = createSelector(
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (state: App.State) => getLuxPlusSubscriptionRenewalDate(state),
  (subscriptionItem, renewalDate): string | undefined => {
    const expiryDate = subscriptionItem?.expiryDate
    const hasActiveLuxPlusSubscriptionRenewal = subscriptionItem?.renewalStatus === 'ALLOW_RENEWAL'
    if (!renewalDate && !expiryDate) return undefined
    if (hasActiveLuxPlusSubscriptionRenewal) return renewalDate
    return expiryDate
  },
)

const isMembershipSubscriptionItemSubscribedTier = (item: App.MemberSubscriptionItem): item is App.MemberSubscriptionItemSubscribedTier => {
  return item.tier !== 'free_preview'
}

export const prioritisedSubscriptionItem = createSelector(
  (state: App.State) => state.auth.account.luxPlus.member.subscription,
  (state: App.State) => getLuxPlusSubscriptionItems(state),
  (memberSubscription, checkoutSubscriptionItems): App.MemberSubscriptionItemSubscribedTier | App.Checkout.LuxPlusSubscriptionItem | undefined => {
    // We always want to prioritise the member subscription item if it exists
    // otherwise we use the first item in the cart isLuxPlusSubscriptionItem that comes from the subscription offer
    // NOTE - if free preview user we want to prioritise the checkoutSubscriptionItems
    return memberSubscription.item && isMembershipSubscriptionItemSubscribedTier(memberSubscription.item) ? memberSubscription.item : checkoutSubscriptionItems[0]
  },
)

export const getCurrentLuxPlusMemberTier = (state: App.State): App.MembershipTier | undefined => state.auth.account.luxPlus.member.subscription.item?.tier

/**
 * Picks the first-time join offer by whether the joining fee is waived. The two variants share the same
 * price and benefits — they differ ONLY in the joining fee.
 *
 * Private on purpose: consumers should go through the named `getBaseTierOfferWith(No)JoiningFee`
 * selectors so intent is explicit and the raw boolean can't be flipped by accident. (The one exception
 * is a pure lib helper that receives offers as an argument and can't reach state — it inlines the find.)
 */
function findFirstTimeOffer(
  offers: Array<App.SubscriptionOffer> | undefined,
  joinFeeWaived: boolean,
): App.SubscriptionOffer | undefined {
  return offers?.find(offer => offer.joinFeeWaived === joinFeeWaived)
}

/** The fee-waived join offer (joining fee = 0) — e.g. promo/cruise-upsell flows that waive the fee. */
export const getBaseTierOfferWithNoJoiningFee = createSelector(
  (state: App.State) => state.luxPlus.products.subscription.offers,
  (offers): App.SubscriptionOffer | undefined => findFirstTimeOffer(offers, true),
)

/** The standard join offer (joining fee applies) — used to advertise/display the join. */
export const getBaseTierOfferWithJoiningFee = createSelector(
  (state: App.State) => state.luxPlus.products.subscription.offers,
  (offers): App.SubscriptionOffer | undefined => findFirstTimeOffer(offers, false),
)

/**
 * @returns A LuxPlus+ tier where inclusions are viewable for the current user i.e.
 *  there is no such thing as a free preview tier inclusion. We default back to the base tier
 */
export function getInclusionsViewableLuxPlusTier(state: App.State): App.MembershipSubscriptionTier {
  const currentLuxPlusTier = getCurrentLuxPlusMemberTier(state) ?? 'base'
  return currentLuxPlusTier === 'free_preview' ? 'base' : currentLuxPlusTier
}

export const getRenewalOffer = createSelector(
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (state: App.State) => state.luxPlus.products.subscription.offers,
  (memberSubscriptionItem, offers): { offer: App.SubscriptionOffer | undefined; isRenewal: boolean } => {
    // The member's resolved renewal offer, computed by the backend; non-null exactly while they are
    // renewable (active or within grace).
    const renewalOffer = memberSubscriptionItem?.currentRenewalOffer
    if (!renewalOffer) {
      return { offer: undefined, isRenewal: false }
    }

    // currentRenewalOffer carries only id/price/tier and can be out of the catalog, so borrow the rest
    // of the App.SubscriptionOffer shape (benefits) from any catalog offer in the same region. Benefits
    // are region-derived (svc-membership's getOfferRegionBenefits keys off region only — not tier, price
    // or join fee), so a same-region offer is a valid benefits template and a region-only match avoids
    // missing a grandfathered member whose exact tier has no in-window catalog offer.
    const template = offers?.find(offer => offer.region === renewalOffer.region)
    if (!template) {
      return { offer: undefined, isRenewal: false }
    }

    const offer: App.SubscriptionOffer = {
      ...template,
      id: renewalOffer.id,
      price: renewalOffer.price,
      tier: renewalOffer.tier,
      joinFee: 0,
      joinFeeWaived: true,
    }

    return { offer, isRenewal: true }
  },
)
